import React, { useState } from 'react';
import {
  Alert,
  Chip,
  CircularProgress,
  Divider,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Business, Check, Close, Person } from '@mui/icons-material';
import { Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';

import {
  IKiBeteiligter,
  IKiCompanyMatch,
  IKiMandantProposal,
  IKiOwnerDecision,
  IKiOwnerMatch,
} from '../Interfaces/IKi';
import { useUebernehmeMandanten } from '../hooks/reactQuerys/KiQueries';

/**
 * Die Beteiligten einer Urkunde als Mandanten übernehmen (#1336).
 *
 * Das Auslesen ist der leichte Teil. Die eigentliche Arbeit ist die **Dublette**:
 * ein doppelt angelegter Mandant fällt erst Wochen später auf, wenn zwei
 * Vorgänge an zwei Karteien hängen, und `LegalClient.Email` muss überdies
 * systemweit eindeutig sein. Deshalb steht bei jedem Beteiligten die Auswahl im
 * Vordergrund — zusammenführen, neu anlegen oder übergehen —, und nicht der
 * ausgelesene Text.
 *
 * Vorausgewählt ist „zusammenführen" nur bei einem eindeutigen Treffer: bei
 * Personen Name **und** Geburtsdatum, bei Gesellschaften Registergericht **und**
 * Registernummer. Ein ähnlicher Firmenname genügt nicht — „Muster GmbH" gibt es
 * reichlich.
 *
 * Angelegt wird erst mit dem Klick. Wer die Karte verwirft, hat nichts erzeugt.
 */

interface IProps {
  proposal: IKiMandantProposal;
}

export const CONFIDENCE_COLOR = {
  hoch: 'success',
  mittel: 'warning',
  niedrig: 'error',
} as const;

export interface IDecision {
  Entscheidung: IKiOwnerDecision;
  idLegalClient: number | null;
}

const isCompany = (
  match: IKiOwnerMatch | IKiCompanyMatch
): match is IKiCompanyMatch => 'RegisterNumber' in match;

/** Wie ein Treffer in der Auswahlliste heißt. */
export const matchLabel = (match: IKiOwnerMatch | IKiCompanyMatch): string => {
  if (isCompany(match)) {
    const register = match.RegisterNumber ? `, ${match.RegisterNumber}` : '';

    return `${match.LastName ?? `Mandant ${match.idLegalClient}`}${register}`;
  }

  const name = [match.FirstName, match.LastName].filter(Boolean).join(' ');
  const geboren =
    match.Birthdate !== null && match.Birthdate !== undefined
      ? `, *${String(match.Birthdate).slice(0, 10)}`
      : '';

  return `${name || `Mandant ${match.idLegalClient}`}${geboren}`;
};

export const initialDecision = (beteiligter: IKiBeteiligter): IDecision => ({
  Entscheidung: beteiligter.Vorschlag ?? 'neu',
  idLegalClient:
    beteiligter.Vorschlag === 'zusammenfuehren'
      ? (beteiligter.idLegalClientVorschlag ?? null)
      : null,
});

export const decisionValue = (decision?: IDecision): string => {
  if (decision === undefined) {
    return 'neu';
  }

  return decision.Entscheidung === 'zusammenfuehren' &&
    decision.idLegalClient !== null
    ? `client:${decision.idLegalClient}`
    : decision.Entscheidung;
};

/** Die ausgelesenen Angaben in einer Zeile — was übernommen würde. */
export const details = (beteiligter: IKiBeteiligter): string => {
  const teile: (string | null | undefined)[] =
    beteiligter.art === 'gesellschaft'
      ? [
          beteiligter.Rechtsform,
          beteiligter.Sitz ? `Sitz ${beteiligter.Sitz}` : null,
          [beteiligter.Registergericht, beteiligter.Registernummer]
            .filter(Boolean)
            .join(' '),
        ]
      : [
          beteiligter.Geburtsname ? `geb. ${beteiligter.Geburtsname}` : null,
          beteiligter.Geburtsdatum
            ? `*${String(beteiligter.Geburtsdatum).slice(0, 10)}`
            : null,
          beteiligter.Geburtsort,
        ];

  const anschrift = [
    [beteiligter.Strasse, beteiligter.Hausnummer].filter(Boolean).join(' '),
    [beteiligter.Postleitzahl, beteiligter.Ort].filter(Boolean).join(' '),
  ]
    .filter(teil => teil !== '')
    .join(', ');

  return [...teile, anschrift].filter(Boolean).join(' · ');
};

export const KiMandantProposalCard: React.FC<IProps> = ({ proposal }) => {
  const navigate = useNavigate();
  const { uebernehmeMandanten, isPendingUebernehmeMandanten } =
    useUebernehmeMandanten();

  const beteiligte = proposal.BeteiligteArray ?? [];

  const [decisions, setDecisions] = useState<IDecision[]>(() =>
    beteiligte.map(initialDecision)
  );
  const [state, setState] = useState<'open' | 'done' | 'dismissed'>('open');
  const [error, setError] = useState<string | null>(null);
  const [angelegt, setAngelegt] = useState<
    { idLegalClient: number; Bezeichnung: string }[]
  >([]);
  const [uebersprungen, setUebersprungen] = useState<string[]>([]);

  if (state === 'dismissed') {
    return null;
  }

  const confidence = proposal.Display.Confidence ?? null;

  const setDecision = (index: number, raw: string) =>
    setDecisions(current =>
      current.map((entry, i) =>
        i !== index
          ? entry
          : raw.startsWith('client:')
            ? {
                Entscheidung: 'zusammenfuehren',
                idLegalClient: Number(raw.slice('client:'.length)),
              }
            : { Entscheidung: raw as IKiOwnerDecision, idLegalClient: null }
      )
    );

  async function handleUebernehmen() {
    setError(null);

    try {
      const result = await uebernehmeMandanten({
        BeteiligteArray: beteiligte.map((beteiligter, index) => ({
          ...beteiligter,
          ...decisions[index],
        })) as unknown as Record<string, unknown>[],
      });

      setAngelegt(result.AngelegtArray ?? []);
      setUebersprungen(result.Uebersprungen ?? []);
      setState('done');
    } catch (exception) {
      const detail =
        exception instanceof Error && exception.message
          ? exception.message.replace(/^\d{3} [^:]+: /, '')
          : '';

      setError(
        detail !== ''
          ? detail
          : 'Die Beteiligten konnten nicht übernommen werden.'
      );
    }
  }

  if (state === 'done') {
    return (
      <Stack spacing={1} sx={{ mt: 1 }}>
        <Alert severity='success'>
          {angelegt.length === 0
            ? 'Es wurde kein neuer Mandant angelegt.'
            : `${angelegt.length} Mandant(en) angelegt.`}
          {angelegt.length > 0 && (
            <Stack
              direction='row'
              spacing={0.5}
              flexWrap='wrap'
              useFlexGap
              sx={{ mt: 0.5 }}
            >
              {angelegt.map(eintrag => (
                <Chip
                  key={`ki-mandant-neu-${eintrag.idLegalClient}`}
                  size='small'
                  label={eintrag.Bezeichnung}
                  onClick={() => navigate(`/clients/${eintrag.idLegalClient}`)}
                />
              ))}
            </Stack>
          )}
        </Alert>
        {uebersprungen.length > 0 && (
          <Alert severity='warning' sx={{ py: 0 }}>
            <Stack component='ul' sx={{ m: 0, pl: 2 }}>
              {uebersprungen.map(zeile => (
                <Typography component='li' variant='caption' key={zeile}>
                  {zeile}
                </Typography>
              ))}
            </Stack>
          </Alert>
        )}
      </Stack>
    );
  }

  return (
    <Paper
      variant='outlined'
      sx={{ p: 1.5, mt: 1, borderColor: 'primary.light' }}
    >
      <Stack spacing={1.5}>
        <Stack direction='row' spacing={1} alignItems='center'>
          <Person fontSize='small' color='primary' />
          <Typography variant='subtitle2' sx={{ flexGrow: 1 }}>
            Beteiligte als Mandanten übernehmen
          </Typography>
          {proposal.Display.Dokumentart !== null &&
            proposal.Display.Dokumentart !== undefined && (
              <Chip
                size='small'
                variant='outlined'
                label={proposal.Display.Dokumentart}
              />
            )}
          {confidence !== null && (
            <Chip
              size='small'
              color={CONFIDENCE_COLOR[confidence]}
              variant='outlined'
              label={`Sicherheit: ${confidence}`}
            />
          )}
        </Stack>

        <Stack divider={<Divider flexItem />} spacing={1}>
          {beteiligte.map((beteiligter, index) => (
            <Stack
              key={`ki-beteiligter-${index}`}
              spacing={0.5}
              sx={{ pt: index === 0 ? 0 : 1 }}
            >
              <Stack direction='row' spacing={1} alignItems='center'>
                {beteiligter.art === 'gesellschaft' ? (
                  <Business fontSize='small' color='action' />
                ) : (
                  <Person fontSize='small' color='action' />
                )}
                <Typography variant='body2' sx={{ fontWeight: 500 }}>
                  {beteiligter.Bezeichnung}
                </Typography>
                {beteiligter.rolle !== null &&
                  beteiligter.rolle !== undefined && (
                    // Die Rolle ist ein Hinweis aus der Urkunde, keine Zuordnung
                    // in DiNo — sie wird nirgends verdrahtet.
                    <Chip
                      size='small'
                      variant='outlined'
                      label={beteiligter.rolle}
                    />
                  )}
              </Stack>

              {details(beteiligter) !== '' && (
                <Typography variant='caption' color='text.secondary'>
                  {details(beteiligter)}
                </Typography>
              )}

              <TextField
                select
                size='small'
                sx={{ maxWidth: 420 }}
                value={decisionValue(decisions[index])}
                onChange={event => setDecision(index, event.target.value)}
              >
                <MenuItem value='neu'>Als neuen Mandanten anlegen</MenuItem>
                <MenuItem value='ignorieren'>Nicht übernehmen</MenuItem>
                {(beteiligter.MatchArray ?? []).map(match => (
                  <MenuItem
                    key={`ki-match-${index}-${match.idLegalClient}`}
                    value={`client:${match.idLegalClient}`}
                  >
                    {`Zusammenführen: ${matchLabel(match)}`}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
          ))}
        </Stack>

        {proposal.Hinweise.length > 0 && (
          <Alert severity='info' sx={{ py: 0 }}>
            <Stack component='ul' sx={{ m: 0, pl: 2 }}>
              {proposal.Hinweise.map(hinweis => (
                <Typography component='li' variant='caption' key={hinweis}>
                  {hinweis}
                </Typography>
              ))}
            </Stack>
          </Alert>
        )}

        {proposal.Display.Reason !== null &&
          proposal.Display.Reason !== undefined && (
            <Typography variant='caption' color='text.secondary'>
              {proposal.Display.Reason}
            </Typography>
          )}

        {error !== null && <Alert severity='error'>{error}</Alert>}

        <Stack direction='row' spacing={1} justifyContent='flex-end'>
          <Button
            size='small'
            color='inherit'
            startIcon={<Close />}
            disabled={isPendingUebernehmeMandanten}
            onClick={() => setState('dismissed')}
          >
            Verwerfen
          </Button>
          <Button
            size='small'
            variant='contained'
            startIcon={
              isPendingUebernehmeMandanten ? (
                <CircularProgress size={16} color='inherit' />
              ) : (
                <Check />
              )
            }
            disabled={isPendingUebernehmeMandanten}
            onClick={() => void handleUebernehmen()}
          >
            Übernehmen
          </Button>
        </Stack>

        <Typography variant='caption' color='text.secondary'>
          Es wurde noch nichts angelegt. Die ausgelesenen Angaben sind ein
          Vorschlag zum Abtippen — bitte gegen die Urkunde prüfen.
        </Typography>
      </Stack>
    </Paper>
  );
};
